import { FormEvent, FC, useState } from 'react';

import { InputField } from '@zoopla-eng/zds-components/input-field';
import { Select, type SelectOption } from '@zoopla-eng/zds-components/select';
import { useAbortSignal } from '@zoopla/shared/async-hooks';

import { POSTCODE_REGEX } from '../../helpers/address';
import {
    Address,
    getAddressesForPostcode,
} from './helpers/getAddressesForPostcode';
import { containerStyle, formStyle } from './AddressSelection.styles.css';
import {
    type ButtonAlignmentWithBreakpoints,
    type ButtonCategory,
    type ButtonProps,
    ButtonWithWrapper,
} from './ButtonWithWrapper';

const DEFAULT_ADDRESS_SELECT_OPTION: SelectOption = {
    text: 'Please select an address',
    id: '',
};

type AddressSelectionError = 'postcode' | 'addresses' | 'selected_address';

export type AddressSelectionSuccessHandler = (selectedAddress: Address) => void;

export type AddressSelectionProps = {
    id: string;
    onSuccess: AddressSelectionSuccessHandler;
    addressCtaOverrides?: Partial<ButtonProps>;
    postcodeCtaOverrides?: Partial<ButtonProps>;
    ctaCategory?: ButtonCategory;
    ctaAlignment?: ButtonAlignmentWithBreakpoints;
};

export const AddressSelection: FC<AddressSelectionProps> = ({
    id,
    onSuccess,
    addressCtaOverrides = {},
    postcodeCtaOverrides = {},
    ctaCategory = 'primary',
    ctaAlignment,
}) => {
    const abortSignal = useAbortSignal();

    const [postcode, setPostcode] = useState('');
    const [postcodeLoading, setPostcodeLoading] = useState(false);

    const [addresses, setAddresses] = useState<Map<string, Address>>(new Map());
    const [selectedAddressOption, setSelectedAddressOption] = useState(
        DEFAULT_ADDRESS_SELECT_OPTION
    );

    const [errorType, setErrorType] = useState<AddressSelectionError>();

    const hasAddresses = addresses.size > 0;

    const onPostcodeSubmit = async (evt: FormEvent<HTMLFormElement>) => {
        // If there are addresses, then the user is submitting the form again
        // with the same postcode, so we can ignore it.
        if (hasAddresses) return;

        evt.preventDefault();
        setPostcodeLoading(true);

        if (!postcode.match(POSTCODE_REGEX) || postcode === '') {
            setErrorType('postcode');
            setPostcodeLoading(false);
            return;
        }

        try {
            const results = await getAddressesForPostcode(postcode, {
                signal: abortSignal.current,
            });

            if (!results?.size) {
                throw new Error();
            }

            if (!abortSignal.current?.aborted) {
                setAddresses(results);
                setPostcodeLoading(false);
            }
        } catch {
            if (!abortSignal.current?.aborted) {
                setErrorType('addresses');
                setPostcodeLoading(false);
            }
        }
    };

    let postcodeErrorMessage: string | undefined;

    if (errorType === 'postcode') {
        postcodeErrorMessage = 'Please enter a valid UK postcode';
    } else if (errorType === 'addresses') {
        postcodeErrorMessage =
            "Sorry, we're unable to find any addresses for this postcode";
    }

    const addressOptions: SelectOption[] = [...addresses.values()].map(
        (address) => ({
            id: address.uprn,
            text: address.fullAddress,
        })
    );

    const onAddressSubmit = (evt: FormEvent<HTMLFormElement>) => {
        evt.preventDefault();
        const selectedAddress = addresses.get(selectedAddressOption.id);

        if (!selectedAddress) {
            setErrorType('selected_address');
            return;
        }

        onSuccess(selectedAddress);
    };

    return (
        <div className={containerStyle} role="group">
            <form
                id={`address-selection-postcode-${id}`}
                onSubmit={onPostcodeSubmit}
                data-cs-mask
                className={formStyle}
            >
                <InputField
                    type="text"
                    // It is crucial that we have a unique id for this input
                    // as there is a good chance that this component can
                    // co-exist with another input#postcode, and if that happens
                    // the duplicate IDs mean that the labels are assigned to
                    // both which is an a11y violation, but also breaks our
                    // tests in ways that are very difficult to debug!
                    name={`address-selection-postcode-input-${id}`}
                    label="Enter a full UK postcode"
                    placeholder="e.g. SW1 0RG"
                    value={postcode}
                    autoComplete="postal-code"
                    onChange={(evt) => {
                        setPostcode(evt.target.value);
                        setErrorType(undefined);
                        setAddresses(new Map());
                        setSelectedAddressOption(DEFAULT_ADDRESS_SELECT_OPTION);
                    }}
                    errorText={postcodeErrorMessage}
                />
                {!hasAddresses && (
                    <ButtonWithWrapper
                        alignment={ctaAlignment}
                        text={postcodeCtaOverrides.text || 'Look up postcode'}
                        category={ctaCategory}
                        loading={postcodeLoading}
                        loadingText="Looking up postcode"
                        Icon={postcodeCtaOverrides.Icon}
                        iconPosition={postcodeCtaOverrides.iconPosition}
                    />
                )}
            </form>

            {hasAddresses && (
                <form
                    id={`address-selection-address-${id}`}
                    onSubmit={onAddressSubmit}
                    data-cs-mask
                    className={formStyle}
                >
                    <Select
                        label="Address"
                        options={[
                            DEFAULT_ADDRESS_SELECT_OPTION,
                            ...addressOptions,
                        ]}
                        selectedOption={selectedAddressOption}
                        onSelectedOptionChange={(evt) => {
                            setSelectedAddressOption(evt);
                            setErrorType(undefined);
                        }}
                        errorText={
                            errorType === 'selected_address'
                                ? 'Please select an address from the options'
                                : undefined
                        }
                    />
                    <ButtonWithWrapper
                        alignment={ctaAlignment}
                        text={addressCtaOverrides.text || 'Next'}
                        category={ctaCategory}
                        Icon={addressCtaOverrides.Icon}
                        iconPosition={addressCtaOverrides.iconPosition}
                    />
                </form>
            )}
        </div>
    );
};

export default AddressSelection;
